import { Controller } from "@hotwired/stimulus"
import { filterRangeOptions } from "../utils/range_filter_utils"

export default class extends Controller {
  static targets = ["minSlider", "maxSlider", "minInput", "maxInput", "track", "form"]

  connect() {
    this._updatingFromSlider = false
    this.syncSlidersToInputs()
    this.updateTrack()
    filterRangeOptions(this.minInputTarget, this.maxInputTarget)
    this._sliderChangeHandler = () => filterRangeOptions(this.minInputTarget, this.maxInputTarget)
    this.minSliderTarget.addEventListener("change", this._sliderChangeHandler)
    this.maxSliderTarget.addEventListener("change", this._sliderChangeHandler)
  }

  disconnect() {
    if (this._sliderChangeHandler) {
      this.minSliderTarget.removeEventListener("change", this._sliderChangeHandler)
      this.maxSliderTarget.removeEventListener("change", this._sliderChangeHandler)
    }
  }

  syncSlidersToInputs() {
    const min = parseInt(this.minSliderTarget.min)
    const max = parseInt(this.minSliderTarget.max)
    const minVal = this.minInputTarget.value
    const maxVal = this.maxInputTarget.value

    this.minSliderTarget.value = (minVal === "" || minVal === undefined) ? min : parseInt(minVal)
    this.maxSliderTarget.value = (maxVal === "" || maxVal === undefined) ? max : parseInt(maxVal)
  }

  updateFromSlider(event) {
    const minVal = parseInt(this.minSliderTarget.value)
    const maxVal = parseInt(this.maxSliderTarget.value)

    if (maxVal - minVal < this.getStep()) {
      if (event && event.target === this.minSliderTarget) {
        this.minSliderTarget.value = maxVal - this.getStep()
      } else {
        this.maxSliderTarget.value = minVal + this.getStep()
      }
    }

    this._updatingFromSlider = true
    this.setInputValue(this.minInputTarget, parseInt(this.minSliderTarget.value))
    this.setInputValue(this.maxInputTarget, parseInt(this.maxSliderTarget.value))
    this._updatingFromSlider = false

    this.updateTrack()
  }

  updateFromInput(event) {
    if (this._updatingFromSlider) return
    if (event && event.target.value === "") return

    const min = parseInt(this.minSliderTarget.min)
    const max = parseInt(this.minSliderTarget.max)
    let minVal = parseInt(this.minInputTarget.value)
    let maxVal = parseInt(this.maxInputTarget.value)

    if (!isNaN(minVal)) {
      this.minSliderTarget.value = Math.max(min, Math.min(max, minVal))
    }
    if (!isNaN(maxVal)) {
      this.maxSliderTarget.value = Math.max(min, Math.min(max, maxVal))
    }

    this.updateTrack()
  }

  finalizeInput(event) {
    if (this._updatingFromSlider) return

    const min = parseInt(this.minSliderTarget.min)
    const max = parseInt(this.minSliderTarget.max)
    const rawMin = this.minInputTarget.value
    const rawMax = this.maxInputTarget.value
    const minEmpty = rawMin === "" || rawMin === undefined
    const maxEmpty = rawMax === "" || rawMax === undefined

    if (minEmpty && maxEmpty) {
      this.minSliderTarget.value = min
      this.maxSliderTarget.value = max
      this.updateTrack()
      return
    }

    let minVal = minEmpty ? min : parseInt(rawMin)
    let maxVal = maxEmpty ? max : parseInt(rawMax)

    if (minVal < min) minVal = min
    if (maxVal > max) maxVal = max

    if (minVal > maxVal) {
      if (event && event.target === this.minInputTarget) {
        minVal = maxVal
      } else {
        maxVal = minVal
      }
    }

    if (!minEmpty) this.setInputValue(this.minInputTarget, minVal)
    if (!maxEmpty) this.setInputValue(this.maxInputTarget, maxVal)

    if (minEmpty) this.minSliderTarget.value = min
    if (maxEmpty) this.maxSliderTarget.value = max

    this.updateTrack()
    filterRangeOptions(this.minInputTarget, this.maxInputTarget)
  }

  updateTrack() {
    const min = parseInt(this.minSliderTarget.min)
    const max = parseInt(this.minSliderTarget.max)
    const minVal = parseInt(this.minSliderTarget.value)
    const maxVal = parseInt(this.maxSliderTarget.value)

    if (max - min === 0) return

    const percentMin = ((minVal - min) / (max - min)) * 100
    const percentMax = ((maxVal - min) / (max - min)) * 100

    const isRtl = document.documentElement.dir === 'rtl'
    if (isRtl) {
      this.trackTarget.style.right = (100 - percentMax) + "%"
    } else {
      this.trackTarget.style.left = percentMin + "%"
    }
    this.trackTarget.style.width = (percentMax - percentMin) + "%"
  }

  getStep() {
    return parseInt(this.minSliderTarget.step) || 1
  }

  setInputValue(inputEl, value) {
    if (inputEl.tagName === "SELECT") {
      const closestValue = this.findClosestOption(inputEl, value)
      const ts = inputEl.tomselect
      if (ts) {
        ts.setValue(String(closestValue), true)
      } else {
        inputEl.value = String(closestValue)
      }
      if (inputEl === this.minInputTarget) {
        this.minSliderTarget.value = closestValue
      } else if (inputEl === this.maxInputTarget) {
        this.maxSliderTarget.value = closestValue
      }
    } else {
      inputEl.value = value
      if (inputEl === this.minInputTarget) {
        this.minSliderTarget.value = value
      } else if (inputEl === this.maxInputTarget) {
        this.maxSliderTarget.value = value
      }
    }
  }

  findClosestOption(selectEl, target) {
    const options = Array.from(selectEl.options)
      .map(o => parseInt(o.value))
      .filter(v => !isNaN(v))
    if (!options.length) return target
    return options.reduce((prev, curr) =>
      Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
    )
  }
}